/**
 * AmendmentTimeline — shows chronological contract + amendment history.
 *
 * Requirements: 3.4, 38.5
 */

import { StatusBadge } from '@/components/ui/StatusBadge';
import type { Contract, Amendment } from '@/types';

interface AmendmentTimelineProps {
  contract: Contract;
  amendments: Amendment[];
}

interface TimelineEntry {
  date: string;
  type: 'contract' | 'amendment';
  title: string;
  description: string | null;
  valueImpact: number | null;
  createdBy: string;
}

export function AmendmentTimeline({ contract, amendments }: AmendmentTimelineProps) {
  // Build timeline entries
  const entries: TimelineEntry[] = [];

  // Original contract entry
  entries.push({
    date: contract.effectiveDate ?? contract.createdAt,
    type: 'contract',
    title: 'Original Contract',
    description: contract.contractTitle,
    valueImpact: contract.contractValue,
    createdBy: contract.createdBy,
  });

  // Amendment entries
  for (const amendment of amendments) {
    entries.push({
      date: amendment.amendmentDate,
      type: 'amendment',
      title: `Amendment #${amendment.amendmentId}`,
      description: amendment.description,
      valueImpact: amendment.valueImpact,
      createdBy: amendment.createdBy,
    });
  }

  // Sort chronologically (oldest first)
  entries.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return (
    <div>
      <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>
        Contract Timeline ({amendments.length} amendment{amendments.length !== 1 ? 's' : ''})
      </h2>

      {entries.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No timeline entries.</p>
      ) : (
        <div style={{ position: 'relative', paddingLeft: '1.5rem' }}>
          {/* Vertical line */}
          <div
            style={{
              position: 'absolute',
              left: '0.5rem',
              top: '0.5rem',
              bottom: '0.5rem',
              width: '2px',
              backgroundColor: '#e2e8f0',
            }}
          />

          {entries.map((entry, idx) => (
            <div
              key={idx}
              style={{
                position: 'relative',
                marginBottom: '1rem',
                paddingLeft: '1rem',
              }}
            >
              {/* Dot */}
              <div
                style={{
                  position: 'absolute',
                  left: '-1.15rem',
                  top: '0.35rem',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: entry.type === 'contract' ? '#c8102e' : '#f59e0b',
                  border: '2px solid #fff',
                  boxShadow: '0 0 0 2px #e2e8f0',
                }}
              />

              <div
                style={{
                  padding: '0.75rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  backgroundColor: entry.type === 'contract' ? '#f8fafc' : '#fffbeb',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{entry.title}</span>
                    <StatusBadge
                      label={entry.type === 'contract' ? 'Original' : 'Amendment'}
                      variant={entry.type === 'contract' ? 'blue' : 'yellow'}
                    />
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    {new Date(entry.date).toLocaleDateString()}
                  </span>
                </div>

                {entry.description && (
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#475569' }}>
                    {entry.description}
                  </p>
                )}

                <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                  {entry.valueImpact != null && (
                    <span>
                      {entry.type === 'amendment' ? 'Impact: ' : 'Value: '}
                      {entry.valueImpact >= 0 ? '+' : ''}${Number(entry.valueImpact).toLocaleString()}
                    </span>
                  )}
                  <span>By: {entry.createdBy}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
